import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../supabaseAuth';

const AuditLogPage = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [filters, setFilters] = useState({
    event_type: '',
    status: ''
  });

  const { getAccessToken } = useSupabase();
  const itemsPerPage = 25;

  // Fetch audit logs from API
  const fetchAuditLogs = async (page = 1, appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the access token from Supabase auth
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      const queryParams = new URLSearchParams({
        page: page.toString(),
        per_page: itemsPerPage.toString(),
        ...appliedFilters
      });

      // console.log('Making API call to:', `${apiBaseUrl}/api/admin/audit-logs?${queryParams}`);

      const response = await fetch(`${apiBaseUrl}/api/admin/audit-logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // console.log('API response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log('API response data:', data);
      
      setAuditLogs(data.audit_logs || []);
      setTotalPages(data.total_pages || 1);
      setTotalEntries(data.total_entries || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // console.log('AuditLogPage mounted, fetching audit logs...');
    fetchAuditLogs(1);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchAuditLogs(newPage);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    // Automatically apply filters when changed
    fetchAuditLogs(1, newFilters);
  };


  const refreshData = () => {
    fetchAuditLogs(currentPage, filters);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-file-text me-2"></i>
            Audit Log
          </h2>
          <p className="text-muted mb-0">
            {totalEntries} total entries • Page {currentPage} of {totalPages}
          </p>
        </div>
        <button 
          className="btn btn-outline-primary"
          onClick={refreshData}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Event Type</label>
              <select 
                className="form-select"
                value={filters.event_type}
                onChange={(e) => handleFilterChange('event_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="subscription_created">Subscription Created</option>
                <option value="subscription_updated">Subscription Updated</option>
                <option value="payment_processed">Payment Processed</option>
                <option value="delivery_created">Deliveries</option>
                <option value="reports">Reports</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select 
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error loading audit logs: {error}
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{width: '180px'}}>Timestamp</th>
                  <th style={{width: '120px'}}>Event Type</th>
                  <th style={{width: '80px'}}>Status</th>
                  <th>Message</th>
                  <th style={{width: '100px'}}>Exec Time</th>
                  <th style={{width: '120px'}}>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      <i className="bi bi-inbox display-4 d-block mb-2"></i>
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <small className="text-muted">
                          {log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}
                        </small>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {log.event_type || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          log.status === 'success' ? 'bg-success' :
                          log.status === 'error' ? 'bg-danger' :
                          log.status === 'warning' ? 'bg-warning' :
                          'bg-secondary'
                        }`}>
                          {log.status || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <small>{log.message || 'No message'}</small>
                      </td>
                      <td>
                        <small className="text-muted">
                          {log.execution_time ? 
                            (log.execution_time >= 1000 ? 
                              `${(log.execution_time / 1000).toFixed(2)}s` : 
                              `${log.execution_time.toFixed(2)}ms`
                            ) : 'N/A'
                          }
                        </small>
                      </td>
                      <td>
                        <small className="font-monospace">
                          {log.ip_address || 'N/A'}
                        </small>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              
              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNum = Math.max(1, currentPage - 2) + index;
                if (pageNum <= totalPages) {
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                }
                return null;
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
